import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface AuthUser {
  email: string;
  firstName: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  currentUser: AuthUser;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  setCurrentUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const API_BASE_URL = process.env.REACT_APP_API_HOST;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthUser>({ email: '', firstName: '' });

  useEffect(() => {
    const stored = localStorage.getItem('isAuthenticated') === 'true';
    const userJson = localStorage.getItem('user');
    if (stored && userJson) {
      try {
        const userData = JSON.parse(userJson);
        setIsAuthenticated(true);
        setCurrentUser({ email: userData.email, firstName: userData.firstName });
      } catch {
        setIsAuthenticated(false);
        setCurrentUser({ email: '', firstName: '' });
      }
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Błąd logowania');
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify(data.user));
    setIsAuthenticated(true);
    setCurrentUser({ email: data.user.email, firstName: data.user.firstName });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setCurrentUser({ email: '', firstName: '' });
    window.history.pushState({}, '', '/');
    window.location.href = '/';
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, firstName, lastName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Błąd rejestracji');
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify(data.user));
    setIsAuthenticated(true);
    setCurrentUser({ email: data.user.email, firstName: data.user.firstName });
  }, []);

  const value: AuthContextValue = {
    isAuthenticated,
    currentUser,
    login,
    logout,
    register,
    setCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
